import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout.jsx";
import { accounts as accountsApi, dashboard } from "../lib/supabase.js";

function StatusBadge({ status }) {
  const s = { active: { bg: "#0a2e1a", color: "#34d399", border: "#166534" }, onboarding: { bg: "#2a1f05", color: "#fbbf24", border: "#854d0e" }, suspended: { bg: "#2a0a0a", color: "#f87171", border: "#991b1b" }, churned: { bg: "#1a1a1a", color: "#888", border: "#333" } }[status] || { bg: "#1a1a1a", color: "#888", border: "#333" };
  return <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase", background: s.bg, color: s.color, border: `1px solid ${s.border}` }}><span style={{ width: 6, height: 6, borderRadius: "50%", background: s.color }} />{status}</span>;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [accts, setAccts] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const [s, a, e] = await Promise.all([dashboard.getStats(), accountsApi.list(), dashboard.getEvents(10)]);
      setStats(s);
      setAccts(a.data || []);
      setEvents(e.data || []);
      setLoading(false);
    })();
  }, []);

  if (loading) return <Layout><div style={{ color: "#555", padding: 40 }}>Loading...</div></Layout>;

  return (
    <Layout mrr={stats?.mrr}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>Platform Overview</h1>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: "#555" }}>Real-time platform health and metrics</p>
        </div>
      </div>

      {/* Metrics */}
      <div style={{ display: "flex", gap: 16, marginBottom: 28, flexWrap: "wrap" }}>
        {[
          { label: "Active Accounts", value: stats?.accounts?.active || 0, sub: `${stats?.accounts?.total || 0} total`, color: "#818cf8" },
          { label: "Onboarding", value: stats?.accounts?.onboarding || 0, color: "#fbbf24" },
          { label: "Monthly Revenue", value: `$${(stats?.mrr || 0).toLocaleString()}`, sub: `$${((stats?.mrr || 0) * 12).toLocaleString()} ARR`, color: "#34d399" },
        ].map((m) => (
          <div key={m.label} style={{ background: "#0a0a0f", border: "1px solid #1a1a2e", borderRadius: 12, padding: "20px 24px", flex: 1, minWidth: 170 }}>
            <div style={{ fontSize: 11, color: "#666", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8, fontWeight: 600 }}>{m.label}</div>
            <div style={{ fontSize: 30, fontWeight: 700, color: m.color, lineHeight: 1 }}>{m.value}</div>
            {m.sub && <div style={{ fontSize: 12, color: "#555", marginTop: 6 }}>{m.sub}</div>}
          </div>
        ))}
      </div>

      {/* Accounts */}
      <div style={{ background: "#0a0a0f", border: "1px solid #1a1a2e", borderRadius: 12, marginBottom: 24 }}>
        <div style={{ padding: "16px 24px", borderBottom: "1px solid #1a1a2e", display: "flex", justifyContent: "space-between" }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: "#888" }}>Accounts</span>
          <button onClick={() => navigate("/accounts")} style={{ background: "none", border: "none", color: "#6366f1", fontSize: 12, cursor: "pointer", fontWeight: 600, fontFamily: "inherit" }}>View All →</button>
        </div>
        {accts.length === 0 ? (
          <div style={{ padding: "40px 24px", textAlign: "center", color: "#444", fontSize: 13 }}>No accounts yet. Use the SQL editor to add your first customer.</div>
        ) : accts.map((a) => (
          <div key={a.id} onClick={() => navigate(`/accounts/${a.id}`)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 24px", borderBottom: "1px solid #111", cursor: "pointer" }} onMouseEnter={(e) => e.currentTarget.style.background = "#0f0f1a"} onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div style={{ width: 36, height: 36, borderRadius: 8, background: `${a.widget_color || "#6366f1"}18`, border: `1px solid ${a.widget_color || "#6366f1"}30`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700, color: a.widget_color || "#6366f1" }}>{a.name?.[0]}</div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: "#ccc" }}>{a.name}</div>
                <div style={{ fontSize: 11, color: "#555" }}>{a.domain || a.slug}</div>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <span style={{ fontSize: 12, color: "#555" }}>{a.plan}</span>
              <StatusBadge status={a.status} />
              {a.billing?.[0] && <span style={{ fontSize: 13, color: "#34d399", fontWeight: 600 }}>${(a.billing[0].mrr_cents / 100).toLocaleString()}/mo</span>}
              <span style={{ color: "#333" }}>›</span>
            </div>
          </div>
        ))}
      </div>

      {/* Events */}
      <div style={{ background: "#0a0a0f", border: "1px solid #1a1a2e", borderRadius: 12 }}>
        <div style={{ padding: "16px 24px", borderBottom: "1px solid #1a1a2e" }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: "#888" }}>Recent Activity</span>
        </div>
        {events.length === 0 ? (
          <div style={{ padding: "30px 24px", textAlign: "center", color: "#444", fontSize: 13 }}>No activity yet.</div>
        ) : events.map((e) => (
          <div key={e.id} style={{ padding: "12px 24px", borderBottom: "1px solid #111", display: "flex", gap: 16 }}>
            <span style={{ fontSize: 11, color: "#444", minWidth: 140 }}>{new Date(e.created_at).toLocaleString()}</span>
            <span style={{ fontSize: 13, color: "#ccc" }}>{e.event_type}{e.accounts && ` — ${e.accounts.name}`}</span>
          </div>
        ))}
      </div>
    </Layout>
  );
}
